import { useEffect, useState, type FormEvent } from "react";
import "./paneles.css";

import { encargar, escucharEventos, type AvisoDeTrabajo } from "./api";

/**
 * Encargar y marcharse.
 *
 * Se pide algo a una máquina, se recibe el acuse en el acto —con el número del
 * trabajo— y se puede cerrar la pestaña: cuando termine, el resultado llega
 * aquí por el canal abierto, con su captura y su enlace. Si la pestaña está
 * cerrada, lo verás al volver.
 *
 * Es pequeña a propósito: lo normal es encargar desde el chat, hablando. Esto
 * es para lo que no hace falta pensar —una captura, un análisis—, y para ver
 * que el circuito de ida y vuelta funciona sin pasar por el director.
 */

interface Maquina {
  id: string;
  displayName: string;
  state: string;
  capabilities: string[];
}

const QUE_SE_PUEDE = [
  {
    capacidad: "web.screenshot",
    nombre: "Captura de una página",
    ayuda: "La abre un ordenador y te devuelve la imagen.",
  },
  {
    capacidad: "web.analyze",
    nombre: "Análisis de una web",
    ayuda: "Si responde, cuánto tarda y qué problemas ve.",
  },
] as const;

interface Encargado {
  jobId: string;
  titulo: string;
  desde: number;
  aviso: AvisoDeTrabajo | null;
}

const TERMINADOS = new Set(["succeeded", "dead", "cancelled", "failed"]);

function hace(ms: number): string {
  const s = Math.round((Date.now() - ms) / 1000);
  if (s < 60) return `hace ${s} s`;
  return `hace ${Math.round(s / 60)} min`;
}

export function Encargar() {
  const [maquinas, setMaquinas] = useState<Maquina[]>([]);
  const [capacidad, setCapacidad] = useState<string>(QUE_SE_PUEDE[0].capacidad);
  const [url, setUrl] = useState("");
  const [paginaCompleta, setPaginaCompleta] = useState(false);
  const [maquina, setMaquina] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [encargados, setEncargados] = useState<Encargado[]>([]);
  const [, refrescar] = useState(0);

  useEffect(() => {
    void fetch("/api/workers", { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : { workers: [] }))
      .then((d) => setMaquinas((d as { workers: Maquina[] }).workers));
  }, []);

  // El resultado llega por el canal, sin preguntar.
  useEffect(
    () =>
      escucharEventos({
        onTrabajo: (aviso) =>
          setEncargados((previos) =>
            previos.map((e) => (e.jobId === aviso.jobId ? { ...e, aviso } : e)),
          ),
      }),
    [],
  );

  /*
   * La red de seguridad: si el canal no llega —un proxy que lo corta, el móvil
   * que se durmió—, se pregunta cada cinco segundos por lo que falte. Así un
   * trabajo terminado no se queda pintado como «en marcha» para siempre.
   */
  useEffect(() => {
    const pendientes = encargados.filter((e) => !e.aviso);
    if (pendientes.length === 0) return;
    const t = setInterval(() => {
      refrescar((n) => n + 1);
      for (const e of pendientes) {
        void fetch(`/api/jobs/${e.jobId}`, { credentials: "same-origin" })
          .then((r) => (r.ok ? r.json() : null))
          .then((d) => {
            const job = (d as { job?: { state: string; lastError: string | null } } | null)?.job;
            if (!job || !TERMINADOS.has(job.state)) return;
            setEncargados((previos) =>
              previos.map((x) =>
                x.jobId === e.jobId && !x.aviso
                  ? {
                      ...x,
                      aviso: {
                        jobId: e.jobId,
                        estado: job.state,
                        titulo: e.titulo,
                        resumen: "",
                        error: job.lastError,
                        artefactos: [],
                      },
                    }
                  : x,
              ),
            );
          });
      }
    }, 5_000);
    return () => clearInterval(t);
  }, [encargados]);

  const queHace = QUE_SE_PUEDE.find((q) => q.capacidad === capacidad)!;
  const capaces = maquinas.filter(
    (m) => m.state !== "offline" && m.capabilities.includes(capacidad),
  );

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const direccion = url.trim();
    if (!/^https?:\/\//i.test(direccion)) {
      setError("Pon la dirección entera, empezando por https://");
      return;
    }
    setEnviando(true);
    try {
      const r = await encargar({
        capability: capacidad,
        input: {
          url: direccion,
          ...(capacidad === "web.screenshot" && paginaCompleta ? { paginaCompleta: true } : {}),
        },
        targetWorkerId: maquina || null,
      });
      setEncargados((previos) => [
        { jobId: r.jobId, titulo: `${queHace.nombre}: ${direccion}`, desde: Date.now(), aviso: null },
        ...previos,
      ]);
      setUrl("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo encargar.");
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="pnl">
      <header className="pnl-cabecera">
        <div>
          <h1>Encargar</h1>
          <p className="pnl-sub">
            Una captura o un análisis a un ordenador, sin pasar por el chat. Puedes cerrar la pestaña: el resultado no
            se pierde.
          </p>
        </div>
      </header>

      <div className="en-columnas">
        <form className="pnl-seccion en-form" onSubmit={enviar}>
          <header>
            <h2>Qué encargas</h2>
          </header>
          <div className="en-campos">
            <div className="pnl-vistas en-que" role="group" aria-label="Qué">
              {QUE_SE_PUEDE.map((q) => (
                <button
                  key={q.capacidad}
                  type="button"
                  aria-pressed={capacidad === q.capacidad}
                  onClick={() => setCapacidad(q.capacidad)}
                >
                  {q.nombre}
                </button>
              ))}
            </div>
            <p className="pnl-sub en-ayuda">{queHace.ayuda}</p>

            <label className="en-campo">
              <span>Dirección</span>
              <input
                type="url"
                inputMode="url"
                placeholder="https://bilbolink.com/"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                required
              />
            </label>

            {capacidad === "web.screenshot" && (
              <label className="en-casilla">
                <input
                  type="checkbox"
                  checked={paginaCompleta}
                  onChange={(e) => setPaginaCompleta(e.target.checked)}
                />
                <span>La página entera, no sólo lo que se ve al abrirla</span>
              </label>
            )}

            <label className="en-campo">
              <span>Máquina</span>
              <select value={maquina} onChange={(e) => setMaquina(e.target.value)}>
                <option value="">La primera que pueda</option>
                {capaces.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.displayName}
                  </option>
                ))}
              </select>
            </label>
            {capaces.length === 0 && (
              <p className="tp2-aviso">
                Ninguna máquina conectada sabe hacer esto ahora: se quedará en cola hasta que arranque una.
              </p>
            )}

            {error && (
              <div className="pnl-nota es-mal" role="alert">
                <p>{error}</p>
              </div>
            )}

            <button type="submit" className="pnl-boton si en-enviar" disabled={enviando}>
              {enviando ? "Encargando…" : "Encargar"}
            </button>
          </div>
        </form>

        <section className="pnl-seccion" aria-live="polite">
          <header>
            <h2>Lo encargado</h2>
            {encargados.length > 0 && <span className="pnl-cuenta">{encargados.length}</span>}
          </header>
          {encargados.length === 0 ? (
            <p className="pnl-vacio">Lo que encargues aparece aquí al momento, y su resultado cuando termine.</p>
          ) : (
            encargados.map((e) => {
              const estado = !e.aviso ? "aviso" : e.aviso.estado === "succeeded" ? "ok" : "mal";
              return (
                <article key={e.jobId} className="pnl-fila en-encargado">
                  <span className={`pnl-punto es-${estado}${e.aviso ? "" : " hy-latido"}`} />
                  <div className="txt">
                    <div className="t">{e.titulo}</div>
                    <div className="s">
                      {e.jobId} · {hace(e.desde)}
                    </div>
                    {!e.aviso ? (
                      <div className="s">Encargado. Te lo cuento aquí en cuanto termine.</div>
                    ) : e.aviso.estado === "succeeded" ? (
                      <>
                        <div className="s">Hecho{e.aviso.resumen ? `: ${e.aviso.resumen}` : "."}</div>
                        <div className="en-artefactos">
                          {e.aviso.artefactos.map((a) =>
                            a.esImagen ? (
                              <a key={a.id} href={`/api/artefactos/${a.id}`} target="_blank" rel="noreferrer">
                                <img src={`/api/artefactos/${a.id}`} alt={a.nombre} loading="lazy" />
                              </a>
                            ) : (
                              <a key={a.id} className="pnl-chip" href={`/api/artefactos/${a.id}`} target="_blank" rel="noreferrer">
                                {a.nombre} ↗
                              </a>
                            ),
                          )}
                        </div>
                      </>
                    ) : (
                      <div className="s en-fallo">No salió{e.aviso.error ? `: ${e.aviso.error}` : "."}</div>
                    )}
                  </div>
                </article>
              );
            })
          )}
        </section>
      </div>
    </div>
  );
}
